import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Chip,
  Stack,
  Alert,
  Fade,
  InputAdornment,
  useTheme,
} from '@mui/material';
import {
  CloudUpload,
  Save,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import Header from '../Components/Header';
import StudentSidebar from './StudentSdebar';

const AddCourses = () => {
  const theme = useTheme();
  const [courseData, setCourseData] = useState({
    title: '',
    start_date: '',
    reference_num: '',
    price: '',
    description: '',
    trailer: '',
    categories: [],
  });

  const [categoryInput, setCategoryInput] = useState('');
  const [isInstructor, setIsInstructor] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  useEffect(() => {
    const role = localStorage.getItem('role');
    setIsInstructor(role === 'Instructor' || role === 'Admin');
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCourseData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleTrailerFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCourseData((prevData) => ({
          ...prevData,
          trailer: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCategoryAdd = () => {
    if (categoryInput.trim() && !courseData.categories.includes(categoryInput.trim())) {
      setCourseData((prevData) => ({
        ...prevData,
        categories: [...prevData.categories, categoryInput.trim()],
      }));
      setCategoryInput('');
    }
  };

  const handleCategoryRemove = (indexToRemove) => {
    setCourseData((prevData) => ({
      ...prevData,
      categories: prevData.categories.filter((_, i) => i !== indexToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('jwttoken');
    try {
      await axios.put('https://tdtlworld.com/dss-kaas-backend/lms/courses/', courseData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      Swal.fire({
        title: 'Success!',
        text: 'Course added successfully!',
        icon: 'success',
        confirmButtonColor: '#D4AF37',
      });
      setCourseData({
        title: '',
        start_date: '',
        reference_num: '',
        price: '',
        description: '',
        trailer: '',
        categories: [],
      });
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Failed to add course. Please try again.',
        icon: 'error',
        confirmButtonColor: '#D4AF37',
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header />
      <Box sx={{ display: 'flex', flexGrow: 1, mt: '64px' }}>
        <StudentSidebar onToggle={setSidebarExpanded} />
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: { xs: 2, md: 4 },
            width: sidebarExpanded ? 'calc(100% - 280px)' : 'calc(100% - 80px)',
            transition: theme.transitions.create('width', { duration: 300 })
          }}
        >
          <Container maxWidth="md">
            <Fade in={true} timeout={800}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: { xs: 3, md: 5 }, 
                  borderRadius: '24px', 
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                  <Typography variant="h3" fontWeight={800} gutterBottom>
                    Add New Course
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Fill in the details below to create a new learning module.
                  </Typography>
                </Box>

                {!isInstructor ? (
                  <Alert severity="error" sx={{ borderRadius: '12px' }}>
                    You do not have permission to add courses. Only instructors can access this feature.
                  </Alert>
                ) : (
                  <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                    <Grid container spacing={3}>
                      {/* Basic Info */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Course Title"
                          name="title"
                          value={courseData.title}
                          onChange={handleChange}
                          required
                          placeholder="e.g. Advanced MUI Masterclass"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Start Date"
                          name="start_date"
                          type="date"
                          value={courseData.start_date}
                          onChange={handleChange}
                          required
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Reference Number"
                          name="reference_num"
                          type="number"
                          value={courseData.reference_num}
                          onChange={handleChange}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Price"
                          name="price"
                          type="number"
                          value={courseData.price}
                          onChange={handleChange}
                          required
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Course Description"
                          name="description"
                          value={courseData.description}
                          onChange={handleChange}
                          required
                          placeholder="Describe the course content and learning objectives..."
                        />
                      </Grid>

                      {/* Video Upload */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom fontWeight={700}>
                          Course Trailer (Video)
                        </Typography>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          startIcon={<CloudUpload />}
                          sx={{ 
                            py: 3, 
                            borderStyle: 'dashed', 
                            borderRadius: '12px',
                            justifyContent: 'center'
                          }}
                        >
                          Upload Trailer File
                          <input type="file" hidden accept="video/*" onChange={handleTrailerFile} />
                        </Button>
                        {courseData.trailer && (
                          <Box sx={{ mt: 2, borderRadius: '12px', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                            <video width="100%" controls>
                              <source src={courseData.trailer} type="video/mp4" />
                            </video>
                          </Box>
                        )}
                      </Grid>

                      {/* Categories */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom fontWeight={700}>
                          Categories
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Add a category (e.g. React, UI Design)"
                            value={categoryInput}
                            onChange={(e) => setCategoryInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleCategoryAdd())}
                          />
                          <Button 
                            variant="contained" 
                            onClick={handleCategoryAdd}
                            sx={{ minWidth: '100px' }}
                          >
                            Add
                          </Button>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {courseData.categories.map((cat, index) => (
                            <Chip
                              key={index}
                              label={cat}
                              onDelete={() => handleCategoryRemove(index)}
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          ))}
                        </Stack>
                      </Grid>

                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                          type="submit"
                          startIcon={<Save />}
                          sx={{ 
                            py: 1.5, 
                            borderRadius: '50px', 
                            fontWeight: 800, 
                            fontSize: '1.1rem',
                            boxShadow: '0 8px 25px rgba(212, 175, 55, 0.4)'
                          }}
                        >
                          Save Course
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Fade>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default AddCourses;
